import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  CancelTokenSource,
  Method
} from 'axios'
import { HttpMethod, ContentType, /* CookieType, */ UserType } from '@/common/constant'

import { handleNetworkError /*, getCookie, setCookie */ } from '@/common/common-fn'

// import { AccessTokenResponse } from '@/common/entity.types'

import { apis, getApiUrl } from '@/apis/business/api-config'

import { getAuthToken, setAuthToken, getUserType } from '@/utils/auth'

import { emit, on, off, globalEvents } from '@/utils/event-bus'

const defaultContentType = ContentType.ApplicationJsonUTF8

axios.defaults.headers.post['Content-Type'] = defaultContentType
axios.defaults.headers.patch['Content-Type'] = defaultContentType
axios.defaults.headers.put['Content-Type'] = defaultContentType

export interface IHttpClient {
  requestAsync: (configs: AxiosRequestConfig) => Promise<AxiosResponse<any>>
  getAsync: (url: string, configs: AxiosRequestConfig) => Promise<AxiosResponse<any>>
  postAsync: (url: string, data: object | undefined, configs: AxiosRequestConfig) => Promise<AxiosResponse<any>>
  putAsync: (url: string, data: object | undefined, configs: AxiosRequestConfig) => Promise<AxiosResponse<any>>
  patchAsync: (url: string, data: object | undefined, configs: AxiosRequestConfig) => Promise<AxiosResponse<any>>
  headAsync: (url: string, configs: AxiosRequestConfig) => Promise<AxiosResponse<any>>
  optionsAsync: (url: string, configs: AxiosRequestConfig) => Promise<AxiosResponse<any>>
}

export default class AxiosHttpClient implements IHttpClient {
  #axiosInstance: AxiosInstance
  baseURL: string | undefined
  static isReady: boolean = true // dùng để đánh dấu xem request có sẵn sàng gửi không

  /**
   * @description Khởi tạo với baseURL và timeout
   * @param {*} baseURL
   * @param {*} timeOut
   * @author PHNam
   */
  constructor (baseURL?: string, timeOut?: number | undefined) {
    const axiosConfig: any = {
      timeout: timeOut || 0
    }

    if (baseURL) {
      this.baseURL = baseURL
      axiosConfig.baseURL = baseURL
    }

    this.#axiosInstance = axios.create(axiosConfig)
  }

  /**
   * @description Trả về configs cho axios
   * @abstract
   * @returns
   * @author PHNam
   */
  public getDefaultConfigs (): AxiosRequestConfig {
    return {}
  }

  /**
   * @description Trả về instance của axios
   * @returns
   */
  public getAxiosInstance (): AxiosInstance {
    return this.#axiosInstance
  }

  /**
   * @description
   * @param {*} beforeSendRequest
   * @param {*} onError
   * @author PHNam
   */
  public setRequestInterceptor (
    beforeSendRequest: (
      value: AxiosRequestConfig
    ) => AxiosRequestConfig | Promise<AxiosRequestConfig>,
    onError: (error: any) => any
  ) {
    this.#axiosInstance.interceptors.request.use(beforeSendRequest, onError)
  }

  /**
   * @description
   * @param {*} beforeReturnResponse
   * @param {*} onError
   * @author PHNam
   */
  public setResponseInterceptor (
    beforeReturnResponse: (
      value: AxiosResponse<any>
    ) => AxiosResponse<any> | Promise<AxiosResponse<any>>,
    onError: (error: any) => any
  ) {
    this.#axiosInstance.interceptors.response.use(beforeReturnResponse, onError)
  }

  /**
   * @description Tạo 1 request
   * @param {*} configs
   * @author PHNam
   */
  public requestAsync (
    configs: AxiosRequestConfig
  ): Promise<AxiosResponse<any>> {
    const me = this

    me.transformRequestConfigs(configs)
    const mergeConfigs = me.mergeConfigs(configs)
    return new Promise((resolve, reject) => {
      if (AxiosHttpClient.isReady) {
        me.executeRequestAndRefreshAsync(mergeConfigs)
          .then(result => resolve(result))
          .catch(error => reject(error))
      } else {
        const callback = function () {
          off(globalEvents.ReadySendRequest, callback)
          me.executeRequestAsync(mergeConfigs)
            .then(result => resolve(result))
            .catch(error => reject(error))
        }
        on(globalEvents.ReadySendRequest, callback)
      }
    })
  }

  /**
   * @description tạo request và refresh token nếu có lỗi
   * @param configs
   * @author PVThang
   */
  public executeRequestAndRefreshAsync (
    configs: AxiosRequestConfig
  ): Promise<AxiosResponse<any>> {
    const me = this

    return new Promise((resolve, reject) => {
      me.#axiosInstance.request(configs)
        .then(data => resolve(data))
        .catch((ex) => {
          const mergeConfigs = me.mergeConfigs(configs)
          if (AxiosHttpClient.isReady) {
            AxiosHttpClient.isReady = false
            me.getRefreshToken()
              .then((refreshTokenResponse) => {
                me.handleRefreshTokenResponse(refreshTokenResponse)
              })
              .finally(() => {
                AxiosHttpClient.isReady = true
                emit(globalEvents.ReadySendRequest)
                me.executeRequestAsync(mergeConfigs)
                  .then(res => resolve(res))
                  .catch(err => reject(err))
              })
          } else {
            const callback = function () {
              off(globalEvents.ReadySendRequest, callback)
              me.executeRequestAsync(mergeConfigs)
                .then(res => resolve(res))
                .catch(err => reject(err))
            }
            on(globalEvents.ReadySendRequest, callback)
          }
        })
    })
  }

  /**
   * @description tạo 1 request
   * @param configs
   * @author PVThang
   */
  public executeRequestAsync (
    configs: AxiosRequestConfig
  ): Promise<AxiosResponse<any>> {
    const me = this

    return new Promise((resolve, reject) => {
      me.#axiosInstance.request(me.mergeConfigs(configs))
        .then(data => resolve(data))
        .catch(error => {
          handleNetworkError(error)
          reject(error)
        })
    })
  }

  /**
   * Xử lý response refresh token
   * @param refreshTokenResponse
   */
  // @ts-ignore
  handleRefreshTokenResponse (refreshTokenResponse) {
    const accessToken = refreshTokenResponse?.Data?.AccessToken
    if (accessToken) {
      setAuthToken(accessToken)
    }
  }

  getRefreshToken () {
    const me = this
    const token = getAuthToken()
    const userType = getUserType()
    const controllerName = userType === UserType.Teacher ? 'teachers' : 'students'
    const newConfig = {
      baseURL: getApiUrl(apis.AuthAPI),
      url: `/v1/${controllerName}/token/refreshtoken`,
      method: HttpMethod.POST as Method,
      headers: {
        Authorization: token ? `Bearer ${token}` : ''
      },
      data: {
        refreshToken: null
      }
    } as AxiosRequestConfig

    return me.#axiosInstance.request(newConfig)
  }

  /**
   * @description
   * @param {*} configs
   * @author PHNam
   */
  private transformRequestConfigs (configs: Record<string, any>) {
    const data = configs.data || {}

    configs.headers = {
      'Content-Type': configs.contentType
    }

    switch (configs.contentType) {
      case ContentType.ApplicationJson:
      case ContentType.ApplicationJsonUTF8:
        configs.data = JSON.stringify(data)
        break
      case ContentType.FormMultiPart:
        break
      case ContentType.FormUrlEncoded:
        break
    }
  }

  /**
   * @description Gộp configs default + người dùng
   * @param {*} configs
   * @returns Configs mới
   * @author PHNam
   */
  private mergeConfigs (configs: AxiosRequestConfig): AxiosRequestConfig {
    const defaulConfigs = this.getDefaultConfigs()
    const mergeConfigs =
      defaulConfigs && defaulConfigs.toString() === '[object Object]'
        ? { ...defaulConfigs, ...configs }
        : configs

    return mergeConfigs
  }

  /**
   * @description Tạo GET request
   * @param {*} url
   * @param {*} configs
   * @returns
   * @author PHNam
   */
  public getAsync (
    url: string,
    configs: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<any>> {
    const me = this

    configs.url = url
    configs.method = HttpMethod.GET as Method

    return me.requestAsync(me.mergeConfigs(configs))
  }

  /**
   * @description Tạo POST request
   * @param {*} url
   * @param {*} configs
   * @returns
   * @author PHNam
   */
  public postAsync (
    url: string,
    data: object | undefined,
    configs: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<any>> {
    const me = this

    configs.url = url
    configs.data = data
    configs.method = HttpMethod.POST as Method

    return me.requestAsync(me.mergeConfigs(configs))
  }

  /**
   * @description Tạo PUT request
   * @param {*} url
   * @param {*} configs
   * @returns
   * @author PHNam
   */
  public putAsync (
    url: string,
    data: object | undefined,
    configs: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<any>> {
    const me = this

    configs.url = url
    configs.data = data
    configs.method = HttpMethod.PUT as Method

    return me.requestAsync(me.mergeConfigs(configs))
  }

  /**
   * @description Tạo DELETE request
   * @param {*} url
   * @param {*} configs
   * @returns
   * @author PHNam
   */
  public deleteAsync (
    url: string,
    configs: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<any>> {
    const me = this

    configs.url = url
    configs.method = HttpMethod.DELETE as Method

    return me.requestAsync(me.mergeConfigs(configs))
  }

  /**
   * @description Tạo PATCH request
   * @param {*} url
   * @param {*} configs
   * @returns
   * @author PHNam
   */
  public patchAsync (
    url: string,
    data: object | undefined,
    configs: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<any>> {
    const me = this

    configs.url = url
    configs.data = data
    configs.method = HttpMethod.PATCH as Method

    return me.requestAsync(me.mergeConfigs(configs))
  }

  /**
   * @description Tạo OPTION request
   * @param {*} url
   * @param {*} configs
   * @returns
   * @author PHNam
   */
  public optionsAsync (
    url: string,
    configs: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<any>> {
    const me = this

    configs.url = url
    configs.method = HttpMethod.OPTION as Method

    return me.requestAsync(me.mergeConfigs(configs))
  }

  /**
   * @description Tạo HEAD request
   * @param {*} url
   * @param {*} configs
   * @returns
   * @author PHNam
   */
  public headAsync (
    url: string,
    configs: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<any>> {
    const me = this

    configs.url = url
    configs.method = HttpMethod.HEAD as Method

    return me.requestAsync(me.mergeConfigs(configs))
  }

  /**
   * @description Lấy ra đối tượng chứa token cancel request và function để cancel
   * @returns
   * @author PHNam
   */
  public getRequestCancelToken (): CancelTokenSource {
    const CancelToken = axios.CancelToken
    const source = CancelToken.source() // { token: string, cancel: function}

    return source
  }
}

// Config của Axios
/**
 {
  // `url` is the server URL that will be used for the request
  url: '/user',

  // `method` is the request method to be used when making the request
  method: 'get', // default

  // `baseURL` will be prepended to `url` unless `url` is absolute.
  // It can be convenient to set `baseURL` for an instance of axios to pass relative URLs
  // to methods of that instance.
  baseURL: 'https://some-domain.com/api/',

  // `transformRequest` allows changes to the request data before it is sent to the server
  // This is only applicable for request methods 'PUT', 'POST', 'PATCH' and 'DELETE'
  // The last function in the array must return a string or an instance of Buffer, ArrayBuffer,
  // FormData or Stream
  // You may modify the headers object.
  transformRequest: [function (data, headers) {
    // Do whatever you want to transform the data

    return data;
  }],

  // `transformResponse` allows changes to the response data to be made before
  // it is passed to then/catch
  transformResponse: [function (data) {
    // Do whatever you want to transform the data

    return data;
  }],

  // `headers` are custom headers to be sent
  headers: {'X-Requested-With': 'XMLHttpRequest'},

  // `params` are the URL parameters to be sent with the request
  // Must be a plain object or a URLSearchParams object
  params: {
    ID: 12345
  },

  // `paramsSerializer` is an optional function in charge of serializing `params`
  // (e.g. https://www.npmjs.com/package/qs, http://api.jquery.com/jquery.param/)
  paramsSerializer: function (params) {
    return Qs.stringify(params, {arrayFormat: 'brackets'})
  },

  // `data` is the data to be sent as the request body
  // Only applicable for request methods 'PUT', 'POST', 'DELETE , and 'PATCH'
  // When no `transformRequest` is set, must be of one of the following types:
  // - string, plain object, ArrayBuffer, ArrayBufferView, URLSearchParams
  // - Browser only: FormData, File, Blob
  // - Node only: Stream, Buffer
  data: {
    firstName: 'Fred'
  },

  // syntax alternative to send data into the body
  // method post
  // only the value is sent, not the key
  data: 'Country=Brasil&City=Belo Horizonte',

  // `timeout` specifies the number of milliseconds before the request times out.
  // If the request takes longer than `timeout`, the request will be aborted.
  timeout: 1000, // default is `0` (no timeout)

  // `withCredentials` indicates whether or not cross-site Access-Control requests
  // should be made using credentials
  withCredentials: false, // default

  // `adapter` allows custom handling of requests which makes testing easier.
  // Return a promise and supply a valid response (see lib/adapters/README.md).
  adapter: function (config) {
  },

  // `auth` indicates that HTTP Basic auth should be used, and supplies credentials.
  // This will set an `Authorization` header, overwriting any existing
  // `Authorization` custom headers you have set using `headers`.
  // Please note that only HTTP Basic auth is configurable through this parameter.
  // For Bearer tokens and such, use `Authorization` custom headers instead.
  auth: {
    username: 'janedoe',
    password: 's00pers3cret'
  },

  // `responseType` indicates the type of data that the server will respond with
  // options are: 'arraybuffer', 'document', 'json', 'text', 'stream'
  //   browser only: 'blob'
  responseType: 'json', // default

  // `responseEncoding` indicates encoding to use for decoding responses (Node.js only)
  // Note: Ignored for `responseType` of 'stream' or client-side requests
  responseEncoding: 'utf8', // default

  // `xsrfCookieName` is the name of the cookie to use as a value for xsrf token
  xsrfCookieName: 'XSRF-TOKEN', // default

  // `xsrfHeaderName` is the name of the http header that carries the xsrf token value
  xsrfHeaderName: 'X-XSRF-TOKEN', // default

  // `onUploadProgress` allows handling of progress events for uploads
  // browser only
  onUploadProgress: function (progressEvent) {
    // Do whatever you want with the native progress event
  },

  // `onDownloadProgress` allows handling of progress events for downloads
  // browser only
  onDownloadProgress: function (progressEvent) {
    // Do whatever you want with the native progress event
  },

  // `maxContentLength` defines the max size of the http response content in bytes allowed in node.js
  maxContentLength: 2000,

  // `maxBodyLength` (Node only option) defines the max size of the http request content in bytes allowed
  maxBodyLength: 2000,

  // `validateStatus` defines whether to resolve or reject the promise for a given
  // HTTP response status code. If `validateStatus` returns `true` (or is set to `null`
  // or `undefined`), the promise will be resolved; otherwise, the promise will be
  // rejected.
  validateStatus: function (status) {
    return status >= 200 && status < 300; // default
  },

  // `maxRedirects` defines the maximum number of redirects to follow in node.js.
  // If set to 0, no redirects will be followed.
  maxRedirects: 5, // default

  // `socketPath` defines a UNIX Socket to be used in node.js.
  // e.g. '/var/run/docker.sock' to send requests to the docker daemon.
  // Only either `socketPath` or `proxy` can be specified.
  // If both are specified, `socketPath` is used.
  socketPath: null, // default

  // `httpAgent` and `httpsAgent` define a custom agent to be used when performing http
  // and https requests, respectively, in node.js. This allows options to be added like
  // `keepAlive` that are not enabled by default.
  httpAgent: new http.Agent({ keepAlive: true }),
  httpsAgent: new https.Agent({ keepAlive: true }),

  // `proxy` defines the hostname, port, and protocol of the proxy server.
  // You can also define your proxy using the conventional `http_proxy` and
  // `https_proxy` environment variables. If you are using environment variables
  // for your proxy configuration, you can also define a `no_proxy` environment
  // variable as a comma-separated list of domains that should not be proxied.
  // Use `false` to disable proxies, ignoring environment variables.
  // `auth` indicates that HTTP Basic auth should be used to connect to the proxy, and
  // supplies credentials.
  // This will set an `Proxy-Authorization` header, overwriting any existing
  // `Proxy-Authorization` custom headers you have set using `headers`.
  // If the proxy server uses HTTPS, then you must set the protocol to `https`.
  proxy: {
    protocol: 'https',
    host: '127.0.0.1',
    port: 9000,
    auth: {
      username: 'mikeymike',
      password: 'rapunz3l'
    }
  },

  // `cancelToken` specifies a cancel token that can be used to cancel the request
  // (see Cancellation section below for details)
  cancelToken: new CancelToken(function (cancel) {
  }),

  // `decompress` indicates whether or not the response body should be decompressed
  // automatically. If set to `true` will also remove the 'content-encoding' header
  // from the responses objects of all decompressed responses
  // - Node only (XHR cannot turn off decompression)
  decompress: true // default
}
*/

// Repsonse của Axios
/**
  {
  // `data` is the response that was provided by the server
  data: {},

  // `status` is the HTTP status code from the server response
  status: 200,

  // `statusText` is the HTTP status message from the server response
  statusText: 'OK',

  // `headers` the HTTP headers that the server responded with
  // All header names are lower cased and can be accessed using the bracket notation.
  // Example: `response.headers['content-type']`
  headers: {},

  // `config` is the config that was provided to `axios` for the request
  config: {},

  // `request` is the request that generated this response
  // It is the last ClientRequest instance in node.js (in redirects)
  // and an XMLHttpRequest instance in the browser
  request: {}
}
*/
